import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import CardHeaderOverlay, { CardHeaderOverlayProps } from './CardHeaderOverlay'
import CardBottomOverlay, { CardBottomOverlayProps } from './CardBottomOverlay'
import CardInfo, { CardInfoProps } from './CardInfo'
import { useAutoPlay } from 'core/hooks/useAutoPlay'
import { getPartnerId } from 'core/singleton'
import { RootType } from 'core/store/global/types'

import { useLocation } from 'react-router-dom'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { getLocation, layoutSmall } from 'core/util/default'

export interface CardProps {
  className?: string
  link?: string
  isOpenLink?: boolean
  broadcastId?: string
  broadcastStateCode?: BroadcastStateCode
  thumbnail?: string
  shopLogo?: string
  stream?: string
  small?: boolean
  header?: CardHeaderOverlayProps
  bottom?: CardBottomOverlayProps
  product?: ProductListDetail
  info?: CardInfoProps
  root?: RootType
  isBridge: boolean
  order?: number
  handleClickBroadcast?: ({
    link,
    broadcastId,
    isBridge,
    broadcastTypeCode,
    broadcastName,
    order,
  }: {
    link?: string
    broadcastId?: string
    isBridge?: boolean
    broadcastTypeCode?: string
    broadcastName?: string
    order?: number
  }) => () => void
  handleClickProduct?: ({
    link,
    productId,
    broadcastId,
  }: {
    link: string
    productId: string
    broadcastId: string
  }) => Promise<boolean>
}

const Card: React.FC<CardProps> = (props) => {
  const {
    link,
    thumbnail,
    stream,
    small,
    header,
    bottom,
    product,
    broadcastId,
    broadcastStateCode,
    order,
    info,
    root,
    isBridge,
    handleClickBroadcast,
    handleClickProduct,
  } = props
  const { mediaRef } = useAutoPlay(root ? Boolean(root?.autoPlay) : false)
  const [clickable, setClickable] = useState<boolean>(true)
  const location = useLocation()
  const partnerId = getPartnerId()
  const smallClassName = getPartnerId() === 'lg-uplus' ? 'small_sf' : 'small'
  const broadcastTypeCode = new GAHelper().convertBroadcastStateCodeToBroadcastTypeCode(
    broadcastStateCode,
  )
  // 특정 파트너 사에서 z-index 값을 하향 조정 해달라는 이슈 대응
  const isLowerZIndex = useMemo(() => {
    return partnerId === 'ldfs'
  }, [partnerId])

  const onClickProduct = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!clickable || !link || !product?.productId || !broadcastId || !handleClickProduct) return

    new GAHelper().sendEvent(GA_EVENT.CLICK_PURCHASE, {
      partnersId: partnerId,
      location: getLocation(location),
      broadcastTypeCode: broadcastTypeCode,
      currency: product.currencyId,
      value: product.price,
      items: [
        {
          discount: product.discountPrice.toString(),
          item_brand: '',
          item_id: product.productId,
          item_name: product.productName,
          price: product.price.toString(),
        },
      ],
      affiliation: getLocation(location),
    })

    setClickable(false)
    const isClickable = await handleClickProduct({
      link,
      productId: product?.productId,
      broadcastId,
    })
    setClickable(!!isClickable)
  }

  return (
    <StyledCard
      className={`${small ? smallClassName : undefined} ${props.className}`}
      ref={mediaRef}
      smallClassName={smallClassName}
      isLowerZIndex={isLowerZIndex}
    >
      <div className="card_wrapper">
        <button
          tabIndex={0}
          onClick={handleClickBroadcast?.({
            link,
            broadcastId,
            isBridge,
            broadcastTypeCode,
            broadcastName: info?.title,
            order,
          })}
        >
          <div className="card_media_wrapper">
            <div className="card_media">
              <video
                muted
                playsInline
                data-src={stream}
                className="card_video"
                title={info?.title}
              />
              <div className="card_image" title={info?.title}>
                <img src={thumbnail} alt={info?.title} />
              </div>
            </div>
          </div>
          <CardHeaderOverlay {...header} />
        </button>
        <CardBottomOverlay
          {...bottom}
          {...product}
          small={small}
          clickable={clickable}
          onClickProduct={onClickProduct}
        />
      </div>
      <div className="card_info" tabIndex={0}>
        <CardInfo {...info} broadcastId={broadcastId} small={small} />
      </div>
    </StyledCard>
  )
}

export default Card

const StyledCard = styled.div<{ smallClassName: 'small' | 'small_sf'; isLowerZIndex: boolean }>`
  .card_wrapper {
    display: block;
    border-radius: 6px;
    border: solid 1px ${({ theme }) => theme.thumbnailLineColor};
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    width: 276px;
    height: 368px;
    z-index: ${({ isLowerZIndex }) => (isLowerZIndex ? 5 : 10)};
    ::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 50%;
      z-index: ${({ isLowerZIndex }) => (isLowerZIndex ? 6 : 20)};
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0));
    }
    ::after {
      content: '';
      position: absolute;
      z-index: ${({ isLowerZIndex }) => (isLowerZIndex ? 6 : 20)};
      left: 0;
      right: 0;
      bottom: 0;
      height: 50%;
      background-image: linear-gradient(to top, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0));
    }
    > a,
    > button {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background-color: transparent;
      border: none;
      z-index: ${({ isLowerZIndex }) => (isLowerZIndex ? 7 : 30)};
    }
  }
  .card_info {
    width: 276px;
  }

  .card_info {
    padding: 8px 4px 0px 4px;
  }

  .card_media_wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #000000dd;

    /* align-items: center; */
  }

  .card_media {
    cursor: pointer;
    border-radius: 6px;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    position: absolute;
    flex-direction: column;
    justify-content: center;
    .card_image {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      opacity: 1;
      transition: all ease 2s;
      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
      }
    }
    .card_video {
      width: 100%;
      height: auto;
      position: relative;
      margin-bottom: 0;
      margin-top: 0;
      opacity: 0;
      transition: all ease 2s;
    }

    .card_video.video.loaded {
      opacity: 1;
    }
    .card_video.video.loaded + .card_image {
      opacity: 0;
    }
  }

  ${(props) => `
    &.${props.smallClassName} {
      .card_wrapper {
        width: 180px;
        height: 240px;
      }
      .card_info {
        width: 180px;
        overflow: hidden;
      }
    }
  `}

  ${(props) =>
    layoutSmall(css`
      .card_wrapper {
        width: 213px;
        height: 283px;
      }
      .card_info {
        width: 213px;
        overflow: hidden;
      }
      &.${props.smallClassName} {
        .card_wrapper {
          width: 150px;
          height: 200px;
        }
        .card_info {
          width: 150px;
          overflow: hidden;
        }
      }
    `)}
`
